import React, { useState, lazy, Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProgressSpinner } from 'primereact/progressspinner';
import styled from 'styled-components';
import { ActionButton } from '../../../components/ActionButton';
import { ErrorTooltip } from '../../../components/ErrorTooltip';
import { WithdrawFormData, WithdrawType } from '../../types';
import { validateForm } from '../../utils';
import {
    FormContainer,
    TabsContainer,
    Tab,
    ButtonWrapper
} from '../../styled';
import {
    INITIAL_BANK_FIELDS,
    INITIAL_BUSINESS_FIELDS,
    INITIAL_CARD_FIELDS,
    INITIAL_INDIVIDUAL_FIELDS
} from "components/Finance/WithdrawFunds/constants";

const BusinessForm = lazy(() => import('./BusinessForm').then(module => ({ default: module.BusinessForm })));
const IndividualForm = lazy(() => import('./IndividualForm').then(module => ({ default: module.IndividualForm })));

interface WithdrawFormProps {
    formData: WithdrawFormData;
    onFormChange: (updates: Partial<WithdrawFormData>) => void;
    onSubmit: () => void;
}

export const WithdrawForm: React.FC<WithdrawFormProps> = ({ formData, onFormChange, onSubmit }) => {
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = () => {
        if (!validateForm(formData)) {
            setErrorMessage('Please fill in all required fields');
            setShowError(true);
            setTimeout(() => setShowError(false), 4000);
            return;
        }
        onSubmit();
        setShowError(false);
    };

    const handleChange = (field: keyof WithdrawFormData, value: string) => {
        onFormChange({ [field]: value });
        setShowError(false);
    };

    const handleTypeChange = (type: WithdrawType) => {
        if (type === 'business') {
            onFormChange({
                withdrawType: type,
                ...INITIAL_INDIVIDUAL_FIELDS,
                ...INITIAL_BUSINESS_FIELDS,
            });
        } else {
            onFormChange({
                withdrawType: type,
                ...INITIAL_BUSINESS_FIELDS,
                ...INITIAL_INDIVIDUAL_FIELDS,
            });
        }
        setShowError(false);
    };

    const handleMethodChange = (value: string) => {
        if (value === 'card') {
            onFormChange({
                withdrawalMethod: value,
                ...INITIAL_BANK_FIELDS,
            });
        } else {
            onFormChange({
                withdrawalMethod: value,
                ...INITIAL_CARD_FIELDS,
            });
        }
        setShowError(false);
    };

    return (
        <FormContainer>
            {/* Tabs */}
            <TabsContainer>
                <Tab $active={formData.withdrawType === 'individual'} onClick={() => handleTypeChange('individual')}>
                    <FormattedMessage id="Finance.Withdraw.Individual" defaultMessage="Individual" />
                </Tab>
                <Tab $active={formData.withdrawType === 'business'} onClick={() => handleTypeChange('business')}>
                    <FormattedMessage id="Finance.Withdraw.Business" defaultMessage="Business" />
                </Tab>
            </TabsContainer>

            {/* Form Content with Code Splitting */}
            <Suspense fallback={
                <LoadingBox>
                    <Loading>
                        <ProgressSpinner />
                    </Loading>
                </LoadingBox>
            }>
                {formData.withdrawType === 'business' ? (
                    <BusinessForm
                        formData={formData}
                        onFieldChange={handleChange}
                    />
                ) : (
                    <IndividualForm
                        formData={formData}
                        onFieldChange={handleChange}
                        onMethodChange={handleMethodChange}
                    />
                )}
            </Suspense>

            {/* Submit Button */}
            <ButtonWrapper>
                <ActionButton onClick={handleSubmit} isActive={validateForm(formData)}>
                    <FormattedMessage id="Finance.Withdraw.Submit" defaultMessage="Submit request" />
                </ActionButton>
                {showError && <ErrorTooltip message={errorMessage} />}
            </ButtonWrapper>
        </FormContainer>
    );
};

const LoadingBox = styled.div`
    position: relative;
    height: 70vh;
`;

const Loading = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;